import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { useTestimonials } from "@/hooks/useContent";

export default function TestimonialCarousel({ items }) {
    const { data: loaded } = useTestimonials();
    const list = items ?? loaded;
    const [index, setIndex] = useState(0);
    const count = list.length;

    // Reset when the collection arrives or changes length.
    useEffect(() => setIndex(0), [count]);

    const next = useCallback(() => setIndex((i) => (i + 1) % count), [count]);
    const prev = () => setIndex((i) => (i - 1 + count) % count);

    useEffect(() => {
        const t = setInterval(next, 6000);
        return () => clearInterval(t);
    }, [next]);

    if (!count) {
        return (
            <div className="rounded-3xl bg-white p-10 text-center text-brand-ink/65 shadow-brand">
                No testimonials yet.
            </div>
        );
    }

    const t = list[index];

    return (
        <div className="relative" data-testid="testimonial-carousel">
            <div className="relative overflow-hidden rounded-[28px] rounded-tr-[70px] bg-white p-8 shadow-brand sm:p-12">
                <Quote className="h-12 w-12 text-brand-blue/10" />
                <AnimatePresence mode="wait">
                    <motion.div
                        key={t.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.5 }}
                    >
                        {t.rating && (
                            <div className="mt-2 flex gap-1">
                                {[...Array(t.rating)].map((_, i) => (
                                    <Star key={i} className="h-5 w-5 fill-brand-red text-brand-red" />
                                ))}
                            </div>
                        )}
                        <p className="mt-4 font-display text-xl font-medium leading-relaxed text-brand-blue sm:text-2xl">
                            “{t.content}”
                        </p>
                        <div className="mt-6 flex items-center gap-4">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-blue font-display text-lg font-semibold text-white">
                                {t.name.charAt(0)}
                            </div>
                            <div>
                                <p className="font-semibold text-brand-ink">{t.name}</p>
                                <p className="text-sm text-brand-ink/65">
                                    {t.type}
                                    {t.service ? ` · ${t.service}` : ""}
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>

            <div className="mt-6 flex items-center justify-between">
                <div className="flex">
                    {list.map((_, i) => (
                        // Padding gives each dot a 24px+ touch target without
                        // changing how large it looks.
                        <button
                            key={i}
                            onClick={() => setIndex(i)}
                            aria-label={`Go to testimonial ${i + 1}`}
                            aria-current={i === index ? "true" : undefined}
                            className="group px-2 py-3"
                        >
                            <span
                                className={`block h-2 rounded-full transition-all ${i === index ? "w-8 bg-brand-red" : "w-2 bg-brand-blue/30 group-hover:bg-brand-blue/50"}`}
                            />
                        </button>
                    ))}
                </div>
                <div className="flex gap-2">
                    <button onClick={prev} aria-label="Previous testimonial" data-testid="testimonial-prev" className="flex h-11 w-11 items-center justify-center rounded-full border border-brand-blue/20 text-brand-blue transition-colors hover:bg-brand-blue hover:text-white">
                        <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button onClick={next} aria-label="Next testimonial" data-testid="testimonial-next" className="flex h-11 w-11 items-center justify-center rounded-full border border-brand-blue/20 text-brand-blue transition-colors hover:bg-brand-blue hover:text-white">
                        <ChevronRight className="h-5 w-5" />
                    </button>
                </div>
            </div>
        </div>
    );
}
